import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSelector, useDispatch } from "react-redux";

import { ArrowLeft2, CloseCircle, DocumentUpload, TickCircle, Timer1 } from "iconsax-react";
import TemplateSendPreview from "./TemplateSendPreview";
import ScheduleMessageDialog from "../ScheduleMessageDialog";
import {
  uploadFile,
  clearUploadState,
  sendTemplateMessage,
  clearSendState,
  uploadFileWithMediaID,
  clearUploadMediaIDState,
} from "../../../../redux/Slice/Templates/templateSlice";
import { isSubscriptionBlockedError, checkBeforeOutbound } from "../../../../utils/subscriptionError";
import { getAuthToken } from "../../../../utils/getAuthToken";
import toast from "react-hot-toast";
import { selectUserDetailsData } from "../../../../redux/selectors";
import { slimScrollbarSx } from "../../../../utils/scrollbarStyles";
import { isConsoleVerbose } from "../../../../utils/consoleGate";

const TemplateSendBox = ({ open, onClose, onBack, selectedTemplate }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down("sm"));
  // Squared corners (8px), not MUI's default pill-ish rounded button — Rahim,
  // 2026-08-24: "for the buttons use our new cutted button type", matching
  // the 8px radius used on every card/row this session.
  const footerBtnSx = { width: { xs: "100%", sm: "auto" }, fontSize: { xs: "0.8125rem", sm: "0.875rem" }, py: { xs: 0.75, sm: 1 }, borderRadius: "8px" };

  // Redux states
  const {
    uploadStatus,
    uploadError,
    uploadResult,
    sendStatus,
    sendError,
    uploadMediaIDStatus,
    uploadMediaIDError,
    uploadMediaIDResult,
  } = useSelector((state) => state.template);
  // CRITICAL: Safely handle null messages (for team member chats)
  const messages = useSelector((state) => state.inboxMessage.messages);
  const contactNumber = messages?.contactNumber || null;
  const contactID = messages?.contactID || null;
  const userDetails = useSelector(selectUserDetailsData);
  
  // Check if this is a team member chat (templates are not available for team member chats)

  // Local states
  const [variables, setVariables] = useState({});
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [templatePayload, setTemplatePayload] = useState(null);
  const fileInputRef = useRef(null);

  // New state for carousel card media uploads
  const [carouselMediaUploads, setCarouselMediaUploads] = useState({});
  // Track which card is currently being uploaded
  const [currentUploadingCard, setCurrentUploadingCard] = useState(null);
  // Refs for each card's file input
  const carouselFileInputRefs = useRef({});

  // SNACKBAR states
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // "success" | "error"

  // Check if this is an authentication template
  const isAuthenticationTemplate = selectedTemplate?.category === "AUTHENTICATION" || 
    selectedTemplate?.components?.some(comp => 
      comp.type === "BUTTONS" && 
      comp.buttons?.some(btn => btn.type === "OTP")
    );

  // Get buttons component early
  const buttonsComp = selectedTemplate?.components?.find(
    (c) => c.type === "BUTTONS"
  );

  // On closing the dialog, also clear states
  const handleClose = useCallback(() => {
    dispatch(clearUploadState());
    dispatch(clearSendState());
    dispatch(clearUploadMediaIDState());
    onClose();
  }, [dispatch, onClose]);

  // Whenever sendError changes or sendStatus changes, show a Snackbar if needed
  useEffect(() => {
    if (sendStatus === "failed" && sendError) {
      // If this dialog is not open, the error belongs to another template dialog (e.g. Instagram).
      // Skip our Snackbar to prevent a duplicate notification.
      if (!open) return;
      // Interceptor already toasted for subscription errors; clear Redux state and skip Snackbar
      if (isSubscriptionBlockedError(sendError)) {
        dispatch(clearSendState());
        return;
      }
      // Handle different types of error objects with detailed error information
      let errorMessage = "Failed to send template";
      let errorDetails = "";
      let errorSolution = "";
      
      if (typeof sendError === "string") {
        errorMessage = isAuthenticationTemplate 
          ? `Failed to send OTP: ${sendError}` 
          : `Failed to send template: ${sendError}`;
      } else if (sendError && typeof sendError === "object") {
        // Handle error object with details from backend
        const baseMessage = isAuthenticationTemplate ? "Failed to send OTP" : "Failed to send template";
        
        // Extract user-friendly message
        if (sendError.userFriendlyMessage) {
          errorMessage = sendError.userFriendlyMessage;
        } else if (sendError.message) {
          errorMessage = `${baseMessage}: ${sendError.message}`;
        } else if (sendError.error) {
          errorMessage = `${baseMessage}: ${sendError.error}`;
        } else if (sendError.details) {
          errorMessage = `${baseMessage}: ${sendError.details}`;
        } else {
          // Fallback: try to stringify the error object
          try {
            errorMessage = `${baseMessage}: ${JSON.stringify(sendError)}`;
          } catch (e) {
            errorMessage = `${baseMessage}: Unknown error occurred`;
          }
        }
        
        // Extract additional details
        if (sendError.details && sendError.details !== errorMessage) {
          errorDetails = sendError.details;
        }
        
        // Extract solution if available
        if (sendError.solution) {
          errorSolution = sendError.solution;
        }
        
        // Extract error code for debugging
        if (sendError.errorCode) {
          errorDetails = errorDetails ? `${errorDetails} (Error Code: ${sendError.errorCode})` : `Error Code: ${sendError.errorCode}`;
        }
      }
      
      // Create comprehensive error message
      let fullErrorMessage = errorMessage;
      if (errorDetails) {
        fullErrorMessage += `\n\nDetails: ${errorDetails}`;
      }
      if (errorSolution) {
        fullErrorMessage += `\n\nSolution: ${errorSolution}`;
      }
      
      setSnackbarMessage(fullErrorMessage);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } else if (sendStatus === "succeeded") {
      setSnackbarMessage("Template sent successfully!");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      // Close the dialog after a short delay or immediately
      // We'll do a 1-second delay
      setTimeout(() => {
        handleClose();
      }, 1000);
    }
  }, [sendStatus, sendError, dispatch, open, isAuthenticationTemplate, handleClose]);

  // Close the Snackbar
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  // On closing the dialog, also clear states
  /** Cancel: go back to template list when `onBack` is provided (WhatsApp inbox flow); otherwise close. */
  const handleCancelClick = () => {
    if (typeof onBack === "function") {
      dispatch(clearUploadState());
      dispatch(clearSendState());
      dispatch(clearUploadMediaIDState());
      onBack();
    } else {
      handleClose();
    }
  };

  // ============ HEADER FORMAT CHECK ============
  const headerComp = selectedTemplate?.components?.find(
    (c) => c.type === "HEADER"
  );
  const headerFormat = headerComp?.format?.toUpperCase() || null;
  const isMediaHeader =
    headerFormat && ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerFormat);
  // ============ ACCEPT + EXTENSION LABELS ============
  let acceptStr = "";
  let allowedExtensionsLabel = "";
  if (headerFormat === "DOCUMENT") {
    acceptStr = ".pdf";
    allowedExtensionsLabel = "Allowed: .pdf";
  } else if (headerFormat === "IMAGE") {
    acceptStr = "image/png, image/jpeg";
    allowedExtensionsLabel = "Allowed: .png, .jpg/.jpeg";
  } else if (headerFormat === "VIDEO") {
    acceptStr = "video/mp4";
    allowedExtensionsLabel = "Allowed: .mp4";
  }

  // ============ BODY PLACEHOLDERS ============
  const bodyComp = selectedTemplate?.components?.find(
    (c) => c.type?.toUpperCase() === "BODY"
  );
  const bodyText = bodyComp?.text || "";

  const placeholders = useMemo(() => {
    if (!bodyText) return [];
    const regex = /{{(\d+)}}/g;
    const found = new Set();
    let match;
    while ((match = regex.exec(bodyText)) !== null) {
      found.add(match[1]);
    }
    return [...found].sort((a, b) => Number(a) - Number(b));
  }, [bodyText]);

  // ============ CAROUSEL PLACEHOLDERS ============
  const carouselComp = selectedTemplate?.components?.find(
    (c) => c.type?.toLowerCase() === "carousel"
  );

  // Add functions for carousel media uploads
  const handleSelectCarouselFile = (cardIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Get the card to determine the format
    const card = carouselComp?.cards?.[cardIndex];
    if (!card) return;

    // Find the header component - check for both upper and lowercase
    const cardHeader = card.components?.find(
      (c) => c.type === "header" || c.type === "HEADER"
    );

    if (!cardHeader) {
      setSnackbarMessage(
        `Card ${cardIndex + 1} doesn't have a header component`
      );
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    // Get format - default to IMAGE if not specified
    const format = (cardHeader.format || "image").toUpperCase();

    // Validate file type based on format
    const fileName = file.name.toLowerCase();
    if (format === "DOCUMENT" && !fileName.endsWith(".pdf")) {
      setSnackbarMessage(`Please select a .pdf file for Card ${cardIndex + 1}`);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      if (carouselFileInputRefs.current[cardIndex]) {
        carouselFileInputRefs.current[cardIndex].value = "";
      }
      return;
    } else if (
      format === "IMAGE" &&
      !(
        fileName.endsWith(".png") ||
        fileName.endsWith(".jpg") ||
        fileName.endsWith(".jpeg")
      )
    ) {
      setSnackbarMessage(
        `Please select a .png or .jpg/.jpeg file for Card ${cardIndex + 1}`
      );
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      if (carouselFileInputRefs.current[cardIndex]) {
        carouselFileInputRefs.current[cardIndex].value = "";
      }
      return;
    } else if (format === "VIDEO" && !fileName.endsWith(".mp4")) {
      setSnackbarMessage(`Please select a .mp4 file for Card ${cardIndex + 1}`);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      if (carouselFileInputRefs.current[cardIndex]) {
        carouselFileInputRefs.current[cardIndex].value = "";
      }
      return;
    }

    // Set the currently uploading card index
    setCurrentUploadingCard(cardIndex);

    // Dispatch thunk to upload the file with media ID for carousel
    // Only images and videos are supported for carousel
    if (format === "IMAGE" || format === "VIDEO") {
      dispatch(uploadFileWithMediaID(file));
    } else {
      // For documents or other formats, use the regular upload
      dispatch(uploadFile(file));
    }
  };

  // Trigger hidden input for carousel cards
  const handleCarouselFileButtonClick = (cardIndex) => {
    if (carouselFileInputRefs.current[cardIndex]) {
      carouselFileInputRefs.current[cardIndex].click();
    }
  };

  // Clear uploaded file for a specific carousel card
  const handleClearCarouselFile = (cardIndex) => {
    setCarouselMediaUploads((prev) => {
      const newState = { ...prev };
      delete newState[cardIndex];
      return newState;
    });

    if (carouselFileInputRefs.current[cardIndex]) {
      carouselFileInputRefs.current[cardIndex].value = "";
    }
  };

  // Handle successful file uploads for carousel cards
  useEffect(() => {
    // If upload is successful and we're uploading for a carousel card
    if (
      uploadStatus === "succeeded" &&
      uploadResult &&
      currentUploadingCard !== null
    ) {
      // Store the upload result for this card
      setCarouselMediaUploads((prev) => ({
        ...prev,
        [currentUploadingCard]: uploadResult,
      }));

      // Reset the currently uploading card
      setCurrentUploadingCard(null);

      // Show success message
      setSnackbarMessage(
        `File for Card ${currentUploadingCard + 1} uploaded successfully!`
      );
      setSnackbarSeverity("success");
      setOpenSnackbar(true);

      // Clear upload state for the next upload
      dispatch(clearUploadState());
    }
  }, [uploadStatus, uploadResult, currentUploadingCard, dispatch]);

  // Handle successful media ID uploads for carousel cards
  useEffect(() => {
    // If media ID upload is successful and we're uploading for a carousel card
    if (
      uploadMediaIDStatus === "succeeded" &&
      uploadMediaIDResult &&
      currentUploadingCard !== null
    ) {
      // Store the media ID result for this card
      setCarouselMediaUploads((prev) => ({
        ...prev,
        [currentUploadingCard]: uploadMediaIDResult,
      }));

      // Reset the currently uploading card
      setCurrentUploadingCard(null);

      // Show success message
      setSnackbarMessage(
        `Media for Card ${currentUploadingCard + 1} uploaded successfully!`
      );
      setSnackbarSeverity("success");
      setOpenSnackbar(true);

      // Clear media ID upload state for the next upload
      dispatch(clearUploadMediaIDState());
    } else if (uploadMediaIDStatus === "failed" && uploadMediaIDError) {
      // Handle media ID upload failure
      setSnackbarMessage(
        `Failed to upload media for Card ${
          currentUploadingCard + 1
        }: ${uploadMediaIDError}`
      );
      setSnackbarSeverity("error");
      setOpenSnackbar(true);

      // Reset the currently uploading card
      setCurrentUploadingCard(null);

      // Clear the file input
      if (carouselFileInputRefs.current[currentUploadingCard]) {
        carouselFileInputRefs.current[currentUploadingCard].value = "";
      }

      // Clear media ID upload state for the next upload
      dispatch(clearUploadMediaIDState());
    }
  }, [
    uploadMediaIDStatus,
    uploadMediaIDResult,
    uploadMediaIDError,
    currentUploadingCard,
    dispatch,
  ]);

  // Extract all variables from all carousel cards
  const carouselVariables = useMemo(() => {
    if (!carouselComp || !carouselComp.cards) return [];

    const carouselVars = [];

    carouselComp.cards.forEach((card, cardIndex) => {
      // Find body component in the card
      const cardBody = card.components?.find((c) => c.type === "body");
      if (!cardBody || !cardBody.text) return;

      // Find variables in the body text
      const bodyText = cardBody.text;
      const regex = /{{(\d+)}}/g;
      let match;

      while ((match = regex.exec(bodyText)) !== null) {
        carouselVars.push({
          cardIndex,
          varIndex: match[1],
          key: `card_${cardIndex}_${match[1]}`,
          label: `Card ${cardIndex + 1}, Variable {{${match[1]}}}`,
        });
      }
    });

    return carouselVars;
  }, [carouselComp]);

  // Placeholder changes
  const handleChange = (index, value) => {
    setVariables((prev) => ({ ...prev, [index]: value }));
  };

  // ============ FILE SELECTION + UPLOAD ============
  const handleSelectFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate extension
    const fileName = file.name.toLowerCase();
    if (headerFormat === "DOCUMENT" && !fileName.endsWith(".pdf")) {
      alert("Please select a .pdf file for DOCUMENT type.");
      fileInputRef.current.value = "";
      return;
    } else if (
      headerFormat === "IMAGE" &&
      !(
        fileName.endsWith(".png") ||
        fileName.endsWith(".jpg") ||
        fileName.endsWith(".jpeg")
      )
    ) {
      alert("Please select a .png or .jpg/.jpeg file for IMAGE type.");
      fileInputRef.current.value = "";
      return;
    } else if (headerFormat === "VIDEO" && !fileName.endsWith(".mp4")) {
      alert("Please select a .mp4 file for VIDEO type.");
      fileInputRef.current.value = "";
      return;
    }

    // Dispatch thunk to upload the file
    dispatch(uploadFile(file));
  };

  // Trigger hidden input
  const handleFileButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Clear the uploaded file
  const handleClearFile = () => {
    dispatch(clearUploadState());
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Build template payload (reusable function)
  const buildTemplatePayload = () => {
    const fileLink = uploadResult?.data || "";

    // Create deep copy of selected template for debugging
    const templateDebug = selectedTemplate
      ? JSON.parse(JSON.stringify(selectedTemplate))
      : null;
    console.log("Original template:", templateDebug);
    
    // Check if this is an authentication template
    const isAuthTemplate = selectedTemplate?.category === "AUTHENTICATION" || 
      selectedTemplate?.components?.some(comp => 
        comp.type === "BUTTONS" && 
        comp.buttons?.some(btn => btn.type === "OTP")
      );
    
    console.log("Is authentication template:", isAuthTemplate);
    console.log("Template category:", selectedTemplate?.category);
    console.log("Template components:", selectedTemplate?.components);

    const finalJson = {
      messaging_product: "whatsapp",
      recipient_type: "individual",
      to: contactNumber || "RECIPIENT_PHONE_NUMBER",
      type: "template",
      template: {
        name: selectedTemplate?.name || "example_template_name",
        language: {
          code: selectedTemplate?.language || "en",
        },
        components: [],
      },
    };

    // 1) HEADER if media
    if (isMediaHeader) {
      if (uploadStatus === "succeeded" && fileLink) {
        const fmtLower = headerFormat.toLowerCase();
        const headerParams = { type: fmtLower };

        // Check if fmtLower is "document"
        if (fmtLower === "document") {
          headerParams[fmtLower] = { id: fileLink };
        } else {
          headerParams[fmtLower] = { link: fileLink };
        }

        finalJson.template.components.push({
          type: "header",
          parameters: [headerParams],
        });
      }
    }

    // 2) BODY placeholders
    if (placeholders.length > 0) {
      const bodyParams = placeholders.map((phIndex) => ({
        type: "text",
        text: variables[phIndex] || "",
      }));
      finalJson.template.components.push({
        type: "body",
        parameters: bodyParams,
      });
    }

    // 2.5) AUTHENTICATION TEMPLATE HANDLING
    if (isAuthenticationTemplate) {
      console.log("Processing authentication template placeholders");
      
      // For authentication templates, we need to handle OTP placeholders
      // Check if there are placeholders in the body text
      if (placeholders.length > 0) {
        console.log("Authentication template has placeholders:", placeholders);
        
        // Create body parameters for OTP codes
        const bodyParams = placeholders.map((phIndex) => ({
          type: "text",
          text: variables[phIndex] || "",
        }));
        
        // Add body component if not already added
        if (!finalJson.template.components.some(comp => comp.type === "body")) {
          finalJson.template.components.push({
            type: "body",
            parameters: bodyParams,
          });
        }
        
        // Add button component for OTP (if buttons exist)
        if (buttonsComp && buttonsComp.buttons) {
          const otpButtons = buttonsComp.buttons.filter(btn => btn.type === "OTP");
          if (otpButtons.length > 0) {
            // Add button parameters for OTP
            const buttonParams = placeholders.map((phIndex) => ({
              type: "text",
              text: variables[phIndex] || "",
            }));
            
            finalJson.template.components.push({
              type: "button",
              sub_type: "url",
              index: "0",
              parameters: buttonParams,
            });
          }
        }
      }
    }

    // 3) FOOTER => never included

    // 4) Check for CAROUSEL component
    if (carouselComp && carouselComp.cards && carouselComp.cards.length > 0) {
      console.log(
        "Processing carousel with",
        carouselComp.cards.length,
        "cards"
      );

      // Debug carousel media uploads
      console.log("Carousel media uploads:", carouselMediaUploads);

      // Format the carousel cards for the API
      const formattedCards = carouselComp.cards.map((card, index) => {
        console.log(`Processing card ${index}:`, card);

        // Each card needs these three component types
        let headerComponent = null;
        let bodyComponent = null;
        let buttonComponents = [];

        // Process the card's components array, or handle if it's not available
        const cardComponentsArray = card.components || [];

        // Debug the card components to see what we're working with
        if (isConsoleVerbose) {
        console.log(
          `Card ${index} components array:`,
          JSON.stringify(cardComponentsArray, null, 2)
        );
        }

        if (cardComponentsArray.length === 0) {
          console.warn(`Card ${index} has no components, will use fallback`);
        }

        // 1. Extract header component - check for both upper and lowercase
        const cardHeader = cardComponentsArray.find(
          (c) => c.type === "header" || c.type === "HEADER"
        );

        if (cardHeader) {
          console.log(`Card ${index} has header:`, cardHeader);
          // Format can be either 'IMAGE' or 'image'
          const format = (cardHeader.format || "image").toLowerCase();

          // Check if we have an uploaded media ID for this card
          const cardUploadResult = carouselMediaUploads[index];
          const mediaId =
            cardUploadResult?.mediaId || cardUploadResult?.data || "";

          // Use uploaded media ID if available, otherwise use example data
          const headerData =
            mediaId || cardHeader.example?.header_handle?.[0] || "";

          if (headerData) {
            headerComponent = {
              type: "header",
              parameters: [
                {
                  type: format,
                  [format]: {
                    id: headerData,
                  },
                },
              ],
            };
            console.log(
              `Created header component for card ${index}:`,
              headerComponent
            );
          }
        } else if (card.header_handle) {
          // Fallback if the header is at the card level instead of in components
          const format = "image"; // Default to image

          // Check if we have an uploaded media ID for this card
          const cardUploadResult = carouselMediaUploads[index];
          const mediaId =
            cardUploadResult?.mediaId || cardUploadResult?.data || "";

          headerComponent = {
            type: "header",
            parameters: [
              {
                type: format,
                [format]: {
                  id: mediaId || card.header_handle,
                },
              },
            ],
          };
        }

        // 2. Extract body component - check for both upper and lowercase
        const cardBody = cardComponentsArray.find(
          (c) => c.type === "body" || c.type === "BODY"
        );

        if (cardBody) {
          console.log(`Card ${index} has body:`, cardBody);
          const cardBodyText = cardBody.text || "";
          const bodyVarRegex = /{{(\d+)}}/g;
          const bodyVars = [];
          let match;

          // Check for variables in card body
          while ((match = bodyVarRegex.exec(cardBodyText)) !== null) {
            bodyVars.push(match[1]);
          }

          if (bodyVars.length > 0) {
            // If body has variables, format them for the API
            const bodyParams = bodyVars.map((varIndex) => ({
              type: "text",
              text: variables[`card_${index}_${varIndex}`] || "",
            }));

            bodyComponent = {
              type: "body",
              parameters: bodyParams,
            };
          } else {
            // If no variables, DON'T include the body component at all
            bodyComponent = null;
          }
        } else if (card.text) {
          // Check if the card's text contains variables
          const cardText = card.text || "";
          const bodyVarRegex = /{{(\d+)}}/g;
          const bodyVars = [];
          let match;

          // Check for variables in card text
          while ((match = bodyVarRegex.exec(cardText)) !== null) {
            bodyVars.push(match[1]);
          }

          if (bodyVars.length > 0) {
            // If fallback text has variables, format them for the API
            const bodyParams = bodyVars.map((varIndex) => ({
              type: "text",
              text: variables[`card_${index}_${varIndex}`] || "",
            }));

            bodyComponent = {
              type: "body",
              parameters: bodyParams,
            };
          } else {
            // If no variables, DON'T include the body component
            bodyComponent = null;
          }
        } else {
          // Don't include a body component if there's no text content
          bodyComponent = null;
        }

        // 3. Extract button components - check for both upper and lowercase
        const cardButtons = cardComponentsArray.find(
          (c) => c.type === "buttons" || c.type === "BUTTONS"
        );

        if (cardButtons && cardButtons.buttons) {
          console.log(`Card ${index} has buttons:`, cardButtons);
          cardButtons.buttons.forEach((btn, btnIndex) => {
            // Account for different case in type (QUICK_REPLY vs quick_reply)
            const btnType = (btn.type || "").toLowerCase();
            console.log(`Processing button ${btnIndex}, type:`, btnType);

            if (btnType === "quick_reply" || btnType.includes("quick")) {
              buttonComponents.push({
                type: "button",
                sub_type: "quick_reply",
                index: btnIndex,
                parameters: [
                  {
                    type: "payload",
                    payload: btn.text || "Reply",
                  },
                ],
              });
              console.log(`Added quick_reply button for card ${index}`);
            } else if (btnType === "url" || btnType.includes("url")) {
              const urlValue = btn.url || "https://example.com";
              buttonComponents.push({
                type: "button",
                sub_type: "url",
                index: btnIndex,
                parameters: [
                  {
                    type: "text",
                    text: urlValue,
                  },
                ],
              });
              console.log(`Added url button for card ${index}`);
            } else if (
              btnType === "phone_number" ||
              btnType.includes("phone")
            ) {
              const phoneValue = btn.phone_number || "+123456789";
              buttonComponents.push({
                type: "button",
                sub_type: "phone_number",
                index: btnIndex,
                parameters: [
                  {
                    type: "text",
                    text: phoneValue,
                  },
                ],
              });
              console.log(`Added phone button for card ${index}`);
            }
          });
        } else if (card.buttons) {
          // Fallback if buttons are at the card level instead of in components
          card.buttons.forEach((btn, btnIndex) => {
            const btnType = (btn.type || "").toLowerCase();

            if (btnType === "quick_reply" || btnType.includes("quick")) {
              buttonComponents.push({
                type: "button",
                sub_type: "quick_reply",
                index: btnIndex,
                parameters: [
                  {
                    type: "payload",
                    payload: btn.text || "Reply",
                  },
                ],
              });
            } else if (btnType === "url" || btnType.includes("url")) {
              buttonComponents.push({
                type: "button",
                sub_type: "url",
                index: btnIndex,
                parameters: [
                  {
                    type: "text",
                    text: btn.url || "https://example.com",
                  },
                ],
              });
            } else if (
              btnType === "phone_number" ||
              btnType.includes("phone")
            ) {
              buttonComponents.push({
                type: "button",
                sub_type: "phone_number",
                index: btnIndex,
                parameters: [
                  {
                    type: "text",
                    text: btn.phone_number || "+123456789",
                  },
                ],
              });
            }
          });
        }

        // 4. Build the final components array for this card
        // Start with header if available
        const cardFinalComponents = [];

        if (headerComponent) {
          cardFinalComponents.push(headerComponent);
        }

        // Add body component if available (it may be null if no variables)
        if (bodyComponent) {
          cardFinalComponents.push(bodyComponent);
        }

        // Add button components if available
        cardFinalComponents.push(...buttonComponents);

        if (isConsoleVerbose) {
        console.log(
          `Final card ${index} components:`,
          JSON.stringify(cardFinalComponents, null, 2)
        );
        }
        return {
          card_index: index,
          components: cardFinalComponents,
        };
      });

      // Add carousel component to the payload
      finalJson.template.components.push({
        type: "carousel",
        cards: formattedCards,
      });

      // Final validation before sending
      const carouselComponent = finalJson.template.components.find(
        (comp) => comp.type === "carousel"
      );

      if (carouselComponent) {
        if (isConsoleVerbose) {
        console.log(
          "Final carousel structure:",
          JSON.stringify(carouselComponent, null, 2)
        );
        }

        // Ensure all cards have valid components arrays
        carouselComponent.cards.forEach((card, i) => {
          if (!card.components || !Array.isArray(card.components)) {
            console.error(`Card ${i} has invalid components structure, fixing`);
            card.components = [];
          } else if (card.components.length === 0) {
            console.error(`Card ${i} has empty components array`);
          }

          // Each card must have at least one component, if empty add header
          if (card.components.length === 0) {
            console.error(
              `Card ${i} has no components, adding header placeholder`
            );
            // Try to add a placeholder header since WhatsApp requires at least one component
            const cardUploadResult = carouselMediaUploads[i];
            const mediaId =
              cardUploadResult?.mediaId || cardUploadResult?.data || "";

            if (mediaId) {
              card.components.push({
                type: "header",
                parameters: [
                  {
                    type: "image",
                    image: {
                      id: mediaId,
                    },
                  },
                ],
              });
            }
          }
        });
      }
    } else {
      // Handle non-carousel buttons
      // Add QUICK_REPLY buttons first (if they exist)
      let currentButtonIndex = 0; // Track button indices across all types

      if (buttonsComp && buttonsComp.buttons) {
        const quickReplyButtons = buttonsComp.buttons.filter(
          (b) => (b.type || "").toUpperCase() === "QUICK_REPLY"
        );

        quickReplyButtons.forEach((btn, _) => {
          finalJson.template.components.push({
            type: "button",
            sub_type: "QUICK_REPLY",
            index: currentButtonIndex, // Use the global index counter
            parameters: [
              {
                type: "text",
                text: btn.text || "Reply",
              },
            ],
          });
          currentButtonIndex++; // Increment the index after adding each button
        });

        // Add PHONE_NUMBER buttons second (if they exist)
        const phoneButtons = buttonsComp.buttons.filter(
          (b) => (b.type || "").toUpperCase() === "PHONE_NUMBER"
        );

        phoneButtons.forEach((btn, _) => {
          const phoneNumber = btn.phone_number || "";
          if (phoneNumber && phoneNumber.startsWith("+")) {
            finalJson.template.components.push({
              type: "button",
              sub_type: "VOICE_CALL",
              index: currentButtonIndex, // Use the global index counter
              parameters: [
                {
                  type: "text",
                  text: phoneNumber,
                },
              ],
            });
            currentButtonIndex++; // Increment the index after adding each button
          }
        });

        // URL buttons handling
        const urlButtons = buttonsComp.buttons.filter(
          (b) => (b.type || "").toUpperCase() === "URL"
        );

        urlButtons.forEach((btn, _) => {
          // Check if this is a dynamic URL button
          const isDynamicUrlButton = (btn.url || "").includes("{{1}}");

          if (isDynamicUrlButton) {
            // Get the example value the user entered for this dynamic URL button
            const buttonText =
              variables[`url_${_}`] || btn.text || "Visit Link";

            // For dynamic URLs, use the entered text as the button text
            finalJson.template.components.push({
              type: "button",
              sub_type: "URL",
              index: currentButtonIndex, // Use the global index counter
              parameters: [
                {
                  type: "text",
                  text: buttonText,
                },
              ],
            });
            currentButtonIndex++; // Increment the index after adding each button
          } else {
            // Regular static URL handling (unchanged)
            const originalLink = btn.url || "";
            let finalLink = originalLink;
            const linkRegex = /{{(\d+)}}/g;
            let match;
            while ((match = linkRegex.exec(originalLink)) !== null) {
              const ph = match[1];
              finalLink = finalLink.replace(`{{${ph}}}`, variables[ph] || "");
            }
            if (!finalLink.startsWith("http")) {
              console.warn("Skipping invalid URL button link:", finalLink);
              return;
            }
            finalJson.template.components.push({
              type: "button",
              sub_type: "URL",
              index: currentButtonIndex, // Use the global index counter
              parameters: [
                {
                  type: "text",
                  text: finalLink,
                },
              ],
            });
            currentButtonIndex++; // Increment the index after adding each button
          }
        });
      }
    }

    // Remove components array if empty
    if (finalJson.template.components.length === 0) {
      delete finalJson.template.components;
    }

    // Debug variables
    console.log("Variables being used:", variables);
    console.log("Placeholders found:", placeholders);
    
    return finalJson;
  };

  // ============ BUILD FINAL JSON & DISPATCH sendTemplateMessage ============
  const handleSend = async () => {
    const { blocked, message: blockMsg } = await checkBeforeOutbound(getAuthToken, ['templateSends']);
    if (blocked) { toast.error(blockMsg, { id: 'subscription-blocked', duration: 5000 }); return; }
    const finalJson = buildTemplatePayload();
    // Dispatch the sendTemplateMessage thunk
    if (isConsoleVerbose) {
    console.log("Final sending JSON:", JSON.stringify(finalJson, null, 2));
    }
    // contactId: which chat this is for. The server decides Meta-vs-QR and files the message from it -- by phone alone it can mix up a
    // customer's Meta contact with their QR contact of the same workspace. (The scheduled-send payload is left as it was.)
    dispatch(sendTemplateMessage(contactID ? { ...finalJson, contactId: contactID } : finalJson));
    // We'll handle success/failure in the useEffect above
  };

  // Handle schedule template message
  const handleSchedule = () => {
    const finalJson = buildTemplatePayload();
    // Store template payload and open schedule dialog
    setTemplatePayload(finalJson);
    setScheduleDialogOpen(true);
  };

  // Check if there are dynamic URL variables in the selected template
  const hasDynamicUrlButtons = useMemo(() => {
    if (!buttonsComp?.buttons) return false;
    return buttonsComp.buttons.some(
      (b) =>
        (b.type || "").toUpperCase() === "URL" &&
        (b.url || "").includes("{{1}}")
    );
  }, [buttonsComp]);

  // Special flag to allow sending even when buttons component is missing
  const [overrideUrlButtonCheck, setOverrideUrlButtonCheck] = useState(false);

  // If we have URL variables but no buttons component, set the override
  useEffect(() => {
    if (!hasDynamicUrlButtons) {
      const hasUrlVariables = Object.keys(variables).some((key) =>
        key.startsWith("url_")
      );
      if (hasUrlVariables) {
        setOverrideUrlButtonCheck(true);
      } else {
        setOverrideUrlButtonCheck(false);
      }
    } else {
      setOverrideUrlButtonCheck(false);
    }
  }, [variables, hasDynamicUrlButtons]);

  // ============ RENDER COMPONENT ============
  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        // Bigger — was "md"/500px tall, cramped once the preview card, the
        // variable-fill form, and (for carousel/media templates) an upload
        // control all have to share that space (Rahim, 2026-08-24: "make the
        // card bigger and add proper spacing"). Went to "xl" next, but that
        // was riding on the width:"auto" bug below doing nothing — once that
        // was fixed and the dialog actually reached "xl", it was too wide
        // (Rahim, 2026-08-24: "reduce the width properly"). Back to "lg".
        maxWidth="lg"
        fullWidth
        fullScreen={isXsScreen}
        scroll="paper"
        sx={{
          "& .MuiDialog-paper": {
            padding: 0,
            height: { xs: "min(92vh, 560px)", sm: "min(88vh, 680px)" },
            maxHeight: { xs: "92vh", sm: "min(88vh, 680px)" },
            m: { xs: 1, sm: 2 },
            // Was width:"auto" on sm+ — that overrides fullWidth entirely,
            // so the paper was sizing to its own content instead of actually
            // filling out to the maxWidth="lg" cap, which is why it still
            // read as "very compact" even after widening maxWidth (Rahim,
            // 2026-08-24: "increase the width more... like its parent").
            // Explicit cap just under "lg"'s 1200px now — flat 100% read
            // slightly too wide once it was actually working (Rahim,
            // 2026-08-24: "reduce width more just a little").
            width: { xs: "calc(100% - 16px)", sm: "min(100%, 1080px)" },
            overflow: "hidden",
            borderRadius: { xs: 2, sm: 3 },
          },
          // One place to make every Button in this dialog match the app's
          // squared-corner convention (8px), instead of hunting down each
          // Button's own sx individually across a 1700-line file.
          "& .MuiButton-root": { borderRadius: "8px" },
        }}
      >
        <DialogContent sx={{ padding: 0, overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0 }}>
          <Box
            display="flex"
            flexDirection="column"
            width="100%"
            height="100%"
            gap={{ xs: 1, sm: 2 }}
            sx={{ minHeight: 0, flex: 1 }}
          >
            {/* Title — borderBottom was a literal "grey", invisible-adjacent
                on dark mode instead of theme.divider (same hardcoded-color
                bug class as TemplateSendPreview.jsx had). Heading flattened
                to the Section Title token (1rem/600, no responsive bump) —
                matches every other dialog header built this session
                (TemplateSend.jsx's "Templates", QuickReplyAutocomplete's
                header). Icons swapped to Iconsax. */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              sx={{ p: { xs: 1, sm: 1.5 }, borderBottom: (t) => `1px solid ${t.palette.divider}`, flexShrink: 0 }}
            >
              <Box display="flex" alignItems="center" gap={0.5} flex={1} minWidth={0}>
                {typeof onBack === "function" && (
                  <IconButton
                    size="small"
                    aria-label="Back to template list"
                    onClick={onBack}
                  >
                    <ArrowLeft2 size={isXsScreen ? "18" : "20"} color={theme.palette.text.primary} variant="Linear" />
                  </IconButton>
                )}
                <Typography fontWeight={600} textAlign="center" sx={{ flex: 1, fontSize: "1rem", color: "text.primary" }}>
                  Send Template Message
                </Typography>
              </Box>
              <IconButton size="small" aria-label="Close dialog" onClick={handleClose}>
                <CloseCircle size={isXsScreen ? "18" : "20"} color={theme.palette.text.primary} variant="Linear" />
              </IconButton>
            </Box>

            <Box
              display="flex"
              flex={1}
              flexDirection={{ xs: "column", sm: "row" }}
              gap={{ xs: 1.5, sm: 4 }}
              sx={{ px: { xs: 1, sm: 2 }, minHeight: 0, overflow: "hidden" }}
            >
              {/* Left side: Preview — was overflowY:"visible" + maxHeight:"none"
                  on desktop, inside a parent row with overflow:"hidden". A
                  tall preview (long body + buttons + a carousel) didn't
                  scroll there at all — it just got silently clipped by the
                  parent with no way to see the rest (Rahim, 2026-08-24: "does
                  it skip out of the box"). Now scrolls internally on every
                  breakpoint, same pattern already used in TemplateSend.jsx's
                  own preview pane. */}
              {/* flexBasis:"50%" (not just flex:1 on both) — a literal even
                  split instead of leaving it to flex-grow to arrive at equal
                  widths on its own (Rahim, 2026-08-24: "separate 50 50 left
                  right side"). Sides swapped via `order` on sm+ only (Rahim,
                  2026-08-24: "switch the sides make the preview at right and
                  the texts at left") — DOM order left as-is (safer than
                  physically moving ~400 lines of JSX), mobile stack order
                  untouched since "left/right" doesn't apply there. The
                  divider moved from the form box's borderLeft to this one,
                  since this is now the visual right column. */}
              <Box
                flex={{ xs: "0 0 auto", sm: "1 1 50%" }}
                minWidth={{ xs: 0, sm: 280 }}
                sx={{
                  order: { xs: 0, sm: 2 },
                  maxHeight: { xs: "38vh", sm: "100%" },
                  overflowY: "auto",
                  overflowX: "hidden",
                  WebkitOverflowScrolling: "touch",
                  borderLeft: { xs: "none", sm: `1px solid ${theme.palette.divider}` },
                  pl: { xs: 0, sm: 2 },
                  ...slimScrollbarSx,
                }}
              >
                <TemplateSendPreview />
              </Box>

              <Box
                flex={{ xs: 1, sm: "1 1 50%" }}
                minWidth={{ xs: 0, sm: 280 }}
                display="flex"
                flexDirection="column"
                gap={{ xs: 1.25, sm: 2 }}
                sx={{
                  order: { xs: 1, sm: 1 },
                  borderTop: { xs: `1px solid ${theme.palette.divider}`, sm: "none" },
                  // pl this time (not pr) — padding on the RIGHT edge of the
                  // left column sits right next to the divider, reading as
                  // if it belongs to the preview side. Padding on the LEFT
                  // edge is the actual left-side breathing room (Rahim,
                  // 2026-08-24: "u added the space for the right section...
                  // i told you to do it for the left side").
                  pl: { xs: 0, sm: 2 },
                  pt: { xs: 1, sm: 1 },
                  minHeight: 0,
                  overflowY: "auto",
                  WebkitOverflowScrolling: "touch",
                  ...slimScrollbarSx,
                }}
              >
                {/* Header Upload if needed — was h6/bold responsively bumping
                    to 1.25rem, noticeably heavier/bigger than anything in
                    Inbox/Dashboard (Rahim, 2026-08-24: "use better simple
                    font like we used in inbox and dashboard... better font
                    weight and size"). Section Title token (1rem/600, flat) —
                    same as this dialog's own title bar and every other
                    section header built this session. */}
                {isMediaHeader && (
                  <Box>
                    <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "text.primary" }}>
                      Header File ({headerFormat})
                    </Typography>
                    <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary", mt: 0.25 }}>
                      {allowedExtensionsLabel}
                    </Typography>

                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      mt={1}
                      flexWrap="wrap"
                    >
                      {/* Hidden file input */}
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        accept={acceptStr}
                        onChange={handleSelectFile}
                      />

                      {/*
                        Show different button states based on uploadStatus.
                        Was a red button (idle/failed) / green button
                        (succeeded) — hardcoded colors not in the app's
                        palette anywhere else, plus MUI icons (Rahim,
                        2026-08-24: "use our new icons... dont use coloured
                        button use simple button"). Same plain outlined style
                        (matching the footer's own Cancel button) for every
                        state now — the icon itself (upload vs check) is
                        what signals status, not a red/green button.
                      */}
                      {uploadStatus === "idle" || uploadStatus === "failed" ? (
                        <Button
                          variant="outlined"
                          size={isXsScreen ? "small" : "medium"}
                          startIcon={<DocumentUpload size="18" variant="Linear" />}
                          onClick={handleFileButtonClick}
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                        >
                          Upload File
                        </Button>
                      ) : uploadStatus === "loading" ? (
                        <Button variant="outlined" disabled size={isXsScreen ? "small" : "medium"}>
                          <CircularProgress size="1em" sx={{ mr: 1 }} />
                          Uploading...
                        </Button>
                      ) : uploadStatus === "succeeded" ? (
                        <Button
                          variant="outlined"
                          size={isXsScreen ? "small" : "medium"}
                          startIcon={<TickCircle size="18" variant="Linear" />}
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                        >
                          Uploaded
                        </Button>
                      ) : null}

                      {/* Clear if succeeded or failed */}
                      {(uploadStatus === "succeeded" ||
                        uploadStatus === "failed") && (
                        <IconButton onClick={handleClearFile} size="small">
                          <CloseCircle size="18" variant="Linear" color={theme.palette.text.secondary} />
                        </IconButton>
                      )}
                    </Box>

                    {/* Show upload error if any */}
                    {uploadError && (
                      <Typography variant="body2" color="error" mt={1}>
                        {uploadError.toString()}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* BODY placeholders */}
                {placeholders.length > 0 && (
                  <>
                    <Typography sx={{ mt: { xs: 1, sm: 2 }, fontSize: "1rem", fontWeight: 600, color: "text.primary" }}>
                      Fill in the Variables
                    </Typography>
                    {placeholders.map((index) => (
                      <Box key={index}>
                        <Typography
                          sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "text.secondary", mb: 0.5 }}
                        >
                          {`{{${index}}}`}
                        </Typography>
                        <TextField
                          size="small"
                          variant="outlined"
                          fullWidth
                          value={variables[index] || ""}
                          onChange={(e) => handleChange(index, e.target.value)}
                          placeholder={`Enter value for {{${index}}}`}
                        />
                      </Box>
                    ))}
                  </>
                )}

                {/* Dynamic URL Button Variables */}
                {buttonsComp &&
                  buttonsComp.buttons &&
                  buttonsComp.buttons.some(
                    (b) =>
                      (b.type || "").toUpperCase() === "URL" &&
                      (b.url || "").includes("{{1}}")
                  ) && (
                    <>
                      <Typography sx={{ mt: { xs: 1, sm: 2 }, fontSize: "1rem", fontWeight: 600, color: "text.primary" }}>
                        Dynamic URL Values
                      </Typography>
                      {buttonsComp.buttons
                        .filter(
                          (b) =>
                            (b.type || "").toUpperCase() === "URL" &&
                            (b.url || "").includes("{{1}}")
                        )
                        .map((btn, btnIndex) => (
                          <Box key={`url-btn-${btnIndex}`}>
                            <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "text.secondary", mb: 0.5 }}>
                              {btn.text || `URL Button ${btnIndex + 1}`}
                            </Typography>
                            <TextField
                              size="small"
                              variant="outlined"
                              fullWidth
                              value={variables[`url_${btnIndex}`] || ""}
                              onChange={(e) =>
                                handleChange(`url_${btnIndex}`, e.target.value)
                              }
                              placeholder="Enter example value"
                              helperText={`Example value for ${
                                btn.text || "URL Button"
                              }`}
                            />
                          </Box>
                        ))}
                    </>
                  )}

                {/* Carousel Variables */}
                {carouselVariables.length > 0 && (
                  <>
                    <Typography sx={{ mt: { xs: 1, sm: 2 }, fontSize: "1rem", fontWeight: 600, color: "text.primary" }}>
                      Carousel Variables
                    </Typography>
                    {carouselVariables.map((variable) => (
                      <Box key={variable.key}>
                        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "text.secondary", mb: 0.5 }}>
                          {variable.label}
                        </Typography>
                        <TextField
                          size="small"
                          variant="outlined"
                          fullWidth
                          value={variables[variable.key] || ""}
                          onChange={(e) =>
                            handleChange(variable.key, e.target.value)
                          }
                          placeholder={`Enter value for Card ${
                            variable.cardIndex + 1
                          }, {{${variable.varIndex}}}`}
                        />
                      </Box>
                    ))}
                  </>
                )}

                {/* Carousel Media Upload Section */}
                {carouselComp &&
                  carouselComp.cards &&
                  carouselComp.cards.length > 0 && (
                    <>
                      <Typography sx={{ mt: { xs: 1, sm: 2 }, fontSize: "1rem", fontWeight: 600, color: "text.primary" }}>
                        Carousel Media Upload
                      </Typography>
                      {carouselComp.cards.map((card, cardIndex) => {
                        // Find the header component to determine format
                        const cardHeader = card.components?.find(
                          (c) => c.type === "header" || c.type === "HEADER"
                        );

                        // Skip if no header component
                        if (!cardHeader) return null;

                        // Get format (default to IMAGE)
                        const format = (
                          cardHeader.format || "image"
                        ).toUpperCase();

                        // Set accept string based on format
                        let acceptStr = "";
                        let allowedExtStr = "";
                        if (format === "DOCUMENT") {
                          acceptStr = ".pdf";
                          allowedExtStr = "Allowed: .pdf";
                        } else if (format === "IMAGE") {
                          acceptStr = "image/png, image/jpeg";
                          allowedExtStr = "Allowed: .png, .jpg/.jpeg";
                        } else if (format === "VIDEO") {
                          acceptStr = "video/mp4";
                          allowedExtStr = "Allowed: .mp4";
                        }

                        // Check if this card has been uploaded
                        const hasUpload = carouselMediaUploads[cardIndex];
                        const isUploading =
                          currentUploadingCard === cardIndex &&
                          uploadStatus === "loading";

                        return (
                          <Box
                            key={`card-upload-${cardIndex}`}
                            sx={{ mb: 2, mt: 1 }}
                          >
                            <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "text.primary" }}>
                              {`Card ${cardIndex + 1} (${format})`}
                            </Typography>
                            <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary", mt: 0.25 }}>
                              {allowedExtStr}
                            </Typography>

                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mt={1}
                              flexWrap="wrap"
                            >
                              {/* Hidden file input for this card */}
                              <input
                                type="file"
                                ref={(el) =>
                                  (carouselFileInputRefs.current[cardIndex] =
                                    el)
                                }
                                style={{ display: "none" }}
                                accept={acceptStr}
                                onChange={(e) =>
                                  handleSelectCarouselFile(cardIndex, e)
                                }
                              />

                              {/* Show different button states based on upload
                                  status — same plain-outlined/Iconsax treatment
                                  as the main header upload button above, was
                                  red/orange/green MUI-icon buttons before. */}
                              {!hasUpload ? (
                                <Button
                                  variant="outlined"
                                  size={isXsScreen ? "small" : "medium"}
                                  startIcon={<DocumentUpload size="18" variant="Linear" />}
                                  onClick={() =>
                                    handleCarouselFileButtonClick(cardIndex)
                                  }
                                  sx={{
                                    fontSize: { xs: "0.7rem", sm: "0.875rem" },
                                    whiteSpace: { xs: "normal", sm: "nowrap" },
                                    textAlign: "center",
                                    lineHeight: 1.2,
                                  }}
                                  disabled={isUploading}
                                >
                                  {isUploading ? (
                                    <>
                                      <CircularProgress
                                        size="1em"
                                        sx={{ mr: 1 }}
                                      />
                                      Uploading...
                                    </>
                                  ) : (
                                    `Upload Media for Card ${cardIndex + 1}`
                                  )}
                                </Button>
                              ) : (
                                <Button
                                  variant="outlined"
                                  startIcon={<TickCircle size="18" variant="Linear" />}
                                >
                                  Media Uploaded
                                </Button>
                              )}

                              {/* Clear button for uploaded media */}
                              {hasUpload && (
                                <IconButton
                                  onClick={() =>
                                    handleClearCarouselFile(cardIndex)
                                  }
                                  size="small"
                                  aria-label={`Clear media for card ${cardIndex + 1}`}
                                >
                                  <CloseCircle size="18" variant="Linear" color={theme.palette.text.secondary} />
                                </IconButton>
                              )}
                            </Box>

                            {/* Show media ID for debugging */}
                            {hasUpload && (
                              <Typography
                                variant="caption"
                                sx={{ display: "block", mt: 0.5 }}
                              >
                                Media ID:{" "}
                                {carouselMediaUploads[cardIndex]?.data || "N/A"}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </>
                  )}
              </Box>
            </Box>

            {/* Footer */}
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent="flex-end"
              alignItems={{ xs: "stretch", sm: "center" }}
              gap={{ xs: 1, sm: 2 }}
              mt={{ xs: 0.5, sm: 1 }}
              padding={{ xs: 1.25, sm: 2 }}
              sx={{ flexShrink: 0, borderTop: (t) => `1px solid ${t.palette.divider}` }}
            >
              <Button variant="outlined" onClick={handleCancelClick} sx={footerBtnSx}>
                Cancel
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Timer1 size="18" variant="Linear" />}
                onClick={handleSchedule}
                sx={footerBtnSx}
                disabled={
                  uploadStatus === "loading" ||
                  sendStatus === "loading" ||
                  currentUploadingCard !== null ||
                  uploadMediaIDStatus === "loading" ||
                  (isMediaHeader && uploadStatus !== "succeeded") ||
                  (placeholders.length > 0 &&
                    placeholders.some((index) => !variables[index]))
                }
              >
                Schedule
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSend}
                sx={footerBtnSx}
                disabled={
                  uploadStatus === "loading" ||
                  sendStatus === "loading" ||
                  currentUploadingCard !== null ||
                  uploadMediaIDStatus === "loading" ||
                  (isMediaHeader && uploadStatus !== "succeeded") ||
                  (placeholders.length > 0 &&
                    placeholders.some((index) => !variables[index])) ||
                  (carouselVariables.length > 0 &&
                    carouselVariables.some((v) => !variables[v.key])) ||
                  (hasDynamicUrlButtons &&
                    !overrideUrlButtonCheck &&
                    buttonsComp?.buttons
                      ?.filter(
                        (b) =>
                          (b.type || "").toUpperCase() === "URL" &&
                          (b.url || "").includes("{{1}}")
                      )
                      .some((_, i) => !variables[`url_${i}`])) ||
                  (carouselComp &&
                    carouselComp.cards &&
                    carouselComp.cards.length > 0 &&
                    carouselComp.cards.some((card, index) => {
                      const cardHeader = card.components?.find(
                        (c) => c.type === "header" || c.type === "HEADER"
                      );
                      if (!cardHeader) return false;
                      const format = (cardHeader.format || "").toUpperCase();
                      if (["IMAGE", "VIDEO", "DOCUMENT"].includes(format)) {
                        return !carouselMediaUploads[index];
                      }
                      return false;
                    }))
                }
              >
                {sendStatus === "loading" ? (
                  <CircularProgress size="1em" sx={{ color: "#fff" }} />
                ) : (
                  "Send"
                )}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={openSnackbar}
        onClose={handleCloseSnackbar}
        autoHideDuration={snackbarSeverity === "error" ? 8000 : 3000}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{
            width: "100%",
            maxWidth: { xs: "min(calc(100vw - 32px), 500px)", sm: "500px" },
            fontSize: { xs: "0.8125rem", sm: "0.875rem" },
            whiteSpace: "pre-line" // Allow line breaks in error messages
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Schedule Message Dialog for Templates */}
      <ScheduleMessageDialog
        open={scheduleDialogOpen}
        onClose={() => {
          setScheduleDialogOpen(false);
          setTemplatePayload(null);
        }}
        messageType="template"
        templateData={templatePayload?.template || null}
        templateCustomizations={variables}
        contactId={contactID}
        AccountID={userDetails?.AccountID}
      />
    </>
  );
};

export default TemplateSendBox;
